/**
 * 游客/免费会员 3D 环绕十分钟体验：定时器与 setSurroundEnabled 包装
 * 抽离自 MusicPlayer.js
 */
import { useRef, useCallback, useEffect } from 'react';
import { isPaidMemberOrAdmin } from '../../../utils/playbackGate';

const TEN_MIN_MS = 10 * 60 * 1000;

/**
 * @param {object} options
 * @param {function(boolean): void} options.setSurroundEnabled
 * @param {object|null} options.user
 * @param {boolean} options.isAdmin
 * @param {boolean} options.playing
 * @param {object|null} options.currentMusic
 * @param {boolean} options.surroundEnabled
 * @param {function(string): string} options.t
 * @param {function(string): void} options.showHintPlus
 * @returns {{ setSurroundEnabledWrapped: function(boolean): void }}
 */
export function useSurroundFreeTrial({
  setSurroundEnabled,
  user,
  isAdmin,
  playing,
  currentMusic,
  surroundEnabled,
  t,
  showHintPlus
}) {
  const surroundFreeTrialTimerRef = useRef(null);
  const surroundFreeTrialTimerStartedRef = useRef(false);

  const startSurroundFreeTrialTimer = useCallback(() => {
    if (surroundFreeTrialTimerRef.current) {
      clearTimeout(surroundFreeTrialTimerRef.current);
      surroundFreeTrialTimerRef.current = null;
    }
    surroundFreeTrialTimerRef.current = setTimeout(() => {
      surroundFreeTrialTimerRef.current = null;
      setSurroundEnabled(false);
      showHintPlus(t('player.surround3dFreeTrialEnded'));
    }, TEN_MIN_MS);
  }, [t, setSurroundEnabled, showHintPlus]);

  const setSurroundEnabledWrapped = useCallback(
    (value) => {
      if (surroundFreeTrialTimerRef.current) {
        clearTimeout(surroundFreeTrialTimerRef.current);
        surroundFreeTrialTimerRef.current = null;
      }
      setSurroundEnabled(value);
      if (value === true && !isPaidMemberOrAdmin(user, isAdmin)) {
        showHintPlus(t('player.surround3dFreeTrialHint'));
        startSurroundFreeTrialTimer();
      }
    },
    [user, isAdmin, t, startSurroundFreeTrialTimer, setSurroundEnabled, showHintPlus]
  );

  useEffect(() => {
    if (!playing || !currentMusic) return;
    if (isPaidMemberOrAdmin(user, isAdmin)) return;
    if (!surroundEnabled) return;
    if (surroundFreeTrialTimerStartedRef.current) return;
    surroundFreeTrialTimerStartedRef.current = true;
    startSurroundFreeTrialTimer();
  }, [playing, currentMusic, user, isAdmin, surroundEnabled, startSurroundFreeTrialTimer]);

  useEffect(() => {
    return () => {
      if (surroundFreeTrialTimerRef.current) clearTimeout(surroundFreeTrialTimerRef.current);
    };
  }, []);

  return { setSurroundEnabledWrapped };
}
